import { Injectable } from '@angular/core';
import { Action, State, Store } from '@ngxs/store';
import { AppConfigService } from '../../../app-config.service';
import { PreautorisationApiService } from '../services/preautorisation-api/preautorisation-api.service';
import { PreautorisationModel } from '../models/preautorisation.model';
import {
  GoNextStep,
  GoToStep,
  GoPreviousStep,
  NavigatePageFormulaire,
  NavigatePageAccueil,
  NavigatePageResultat,
  NavigatePageNonEligible,
  SaveAiguilleurResidencePrincipale,
  SaveEtapeTypePropriete,
  SaveEtapeRevenuBrut,
  SaveAiguilleurSalarie,
  SaveEtapeCoemprunteur,
  SaveEtapeCoordonnees,
  SaveEtapeHypotheque,
  SaveEtapePrixPropriete,
  SaveEtapeRevenuLocatif,
  Prefill,
  EditStep,
  CompleteEditStep,
  Submit,
  SubmitSuccess,
  SubmitFailed,
  CancelEditStep,
  Load,
  NavigatePageDetails,
  SaveEtapeConsentement,
  CancelPreautorisation,
  ResetState,
  SaveEtapeMiseDeFonds,
  AccepterContreOffre,
  AccepterContreOffreSuccess,
  AccepterContreOffreFailed,
  RefuserContreOffre,
  RefuserContreOffreSuccess,
  RefuserContreOffreFailed,
  PatchDebug,
  SelectionnerTaux,
  SelectionnerTauxSuccess,
  SelectionnerTauxFailed,
  SaveEtapeAchatADeux,
  SaveAiguilleurMultiproprietaire,
  NavigateDashboard,
  CancelWithConfirmation,
  PrefillFailed,
  PrefillSuccess,
  NavigatePagePreautEnCours,
  NavigatePreautActive,
  ValiderPreadmissibilite,
  NavigatePagePannePreadmissibilite,
  NavigatePageInvitation,
  ValiderCodeInvitationCourrielSuccess,
  ValiderCodeInvitationCourriel,
  RefuserProjet,
  RefuserProjetSuccess,
  ValiderCodeInvitationCourrielFailed,
  RefuserProjetFailed,
  SaveEtapeAchatMemeCoemprunteur,
  NavigatePageAnnuleParCoemprunteur,
  SaveAiguilleurCitoyennete,
  GoToNextInvalidStepOrCompleteEdit
} from './preautorisation.actions';
import { HideLoader, ShowLoader, TriggerErrorPage } from '../../../shared/personne.state';
import { TypeProjet } from '../../../shared/constants/type-projet.enum';
import { StatusPreautorisation } from '../../../shared/modeles/variables';
import { PreautorisationCybermetrieService } from '../services/preautorisation-cybermetrie/preautorisation-cybermetrie.service';
import { cloneDeep, merge } from 'lodash';
import { ModaleAnnulerResultatEnum } from '../components/modale-annuler/modale-annuler-resultat.enum';
import { OpenModalService } from '../../../shared/services/open-modal/open-modal.service';
import { first } from 'rxjs/operators';
import { TypeMultiproprietaireEnum } from '../constants/type-multiproprietaire.enum';
import { StatutPreadmissibilite } from '../models/resultat-preadmissibilite.model';
import { StepEnum } from '../constants/step.enum';
import { FlowHelperService } from '../services/flow-helper/flow-helper.service';
import { ErreurPreautCoemprunteur, ErreurValidationModel } from '../models/erreur-validation.modele';
import { NavigationService } from '../services/navigation/navigation.service';
import { PreautorisationSelectors } from './preautorisation.selectors';
import { TypeEmprunteurEnum } from '../constants/type-emprunteur.enum';
import { TypeProprieteEnum } from '../constants/type-propriete.enum';
import { TypeMultiplexEnum } from '../constants/type-multiplex.enum';
import { MiseDeFondsService } from '../services/mise-de-fonds/mise-de-fonds.service';
import { EventActionEnum } from '../services/preautorisation-cybermetrie/event-action.enum';

const defaultState = {
  currentStep: null,
  isModifying: false,
  isLoaded: false,
  preautorisation: null,
  typePropriete: null,
  typeMultiproprietaire: null,
  isSalarie: null,
  idConsentement: ''
};

export interface PreautorisationStateModel {
  currentStep: StepEnum;
  isModifying: boolean;
  isLoaded: boolean;
  isSalarie?: 'O' | 'N';
  preautorisation: PreautorisationModel;
  idConsentement?: string;
  typeMultiproprietaire?: TypeMultiproprietaireEnum;
  validationError?: ErreurPreautCoemprunteur;
  isResidencePrincipale?: 'O' | 'N';
  isCitoyenCanadien?: 'O' | 'N';
  typePropriete?: TypeProprieteEnum;
  typeMultiplex?: TypeMultiplexEnum;
  stateAvantModification?: PreautorisationStateModel;
}

@Injectable()
@State<PreautorisationStateModel>({
  name: 'preautorisationExperimente',
  defaults: defaultState
})
export class PreautorisationState {
  constructor(
    private readonly _preautorisationApiService: PreautorisationApiService,
    private readonly _appConfigService: AppConfigService,
    private readonly _cybermetrieService: PreautorisationCybermetrieService,
    private readonly _openModalService: OpenModalService,
    private readonly _flowHelperService: FlowHelperService,
    private readonly _navigationService: NavigationService,
    private readonly _store: Store,
    private readonly _miseDeFondsService: MiseDeFondsService
  ) {}

  @Action(NavigatePageFormulaire)
  async navigatePageFormulaire({ dispatch, getState }) {
    const state = getState();
    dispatch(new GoToStep(state.currentStep || this._flowHelperService.steps[0]));
    await this._navigationService.navigatePageFormulaire();
  }

  @Action(NavigatePageAccueil)
  async navigatePageAccueil({ dispatch }) {
    dispatch(new GoToStep(null));
    await this._navigationService.navigatePageAccueil();
  }

  @Action(NavigatePageResultat)
  async navigatePageResultat({ dispatch }) {
    dispatch(new GoToStep(null));
    if (this._store.selectSnapshot(PreautorisationSelectors.isRedirectionPageFonctionInactiveTaux)) {
      await this._navigationService.navigatePageFonctionInactiveTaux();
    } else {
      await this._navigationService.navigatePageResultat();
    }
  }

  @Action(NavigatePageDetails)
  async navigatePageDetails() {
    this._cybermetrieService.pushClickEvent('consultez votre demande');
    await this._navigationService.navigatePageDetails();
  }

  @Action(NavigatePageNonEligible)
  async navigatePageNonEligible() {
    await this._navigationService.navigatePageNonEligible();
  }

  @Action(NavigatePageInvitation)
  async navigatePageInvitation() {
    await this._navigationService.navigatePageInvitation();
  }

  @Action(NavigatePreautActive)
  async navigatePreautActive(_, { typeProjet, statut }: NavigatePreautActive) {
    const showDashboard =
      statut !== StatusPreautorisation.Acceptee && statut !== StatusPreautorisation.ContreOffreAcceptee;
    await this._navigationService.navigateToProjet(typeProjet, showDashboard);
  }

  @Action(NavigatePagePreautEnCours)
  async navigatePagePreautEnCours(_, state: NavigatePagePreautEnCours) {
    await this._navigationService.navigatePagePreautEnCours(state.typeProjet, state.statut, state.institution);
  }

  @Action(NavigateDashboard)
  async navigateDashboard() {
    await this._navigationService.navigateDashboard();
  }

  @Action(NavigatePagePannePreadmissibilite)
  async navigatePagePannePreadmissibilite() {
    await this._navigationService.navigatePagePanne();
  }

  @Action(NavigatePageAnnuleParCoemprunteur)
  async navigatePageAnnuleParCoemprunteur() {
    await this._navigationService.navigatePageAnnuleParCoemprunteur();
  }

  @Action(GoToStep)
  goToStep({ patchState }, { step }: GoToStep) {
    patchState({ currentStep: step });
    window.scroll(0, 0);
  }

  @Action(GoNextStep)
  goNextStep({ dispatch, getState }) {
    const state = getState();
    const nextStep = this._flowHelperService.nextStep;

    if (state.isModifying) {
      dispatch(new GoToNextInvalidStepOrCompleteEdit());
    } else if (nextStep) {
      dispatch(new GoToStep(nextStep));
    } else {
      dispatch(new NavigatePageAccueil());
    }
  }

  @Action(GoPreviousStep)
  goPreviousStep({ dispatch }) {
    const previousStep = this._flowHelperService.previousStep;

    if (previousStep) {
      dispatch(new GoToStep(previousStep));
    } else {
      dispatch(new NavigatePageAccueil());
    }
  }

  @Action(Prefill)
  async prefill({ dispatch }) {
    try {
      dispatch(new ShowLoader());
      const preaut = await this._preautorisationApiService.prefillPreautorisation();
      dispatch(new PrefillSuccess(preaut));
    } catch (error) {
      dispatch(new PrefillFailed(error));
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(PrefillFailed)
  async prefillFailed({ dispatch }, { response }: PrefillFailed) {
    if (response.status === 409) {
      await dispatch(
        new NavigatePagePreautEnCours(
          response.error.type || TypeProjet.PremierAcheteur,
          response.error.statut,
          response.error.institution
        )
      ).toPromise();
    } else {
      dispatch(new TriggerErrorPage());
    }
  }

  @Action(PrefillSuccess)
  async prefillSuccess({ getState, patchState }, { preautorisation }: PrefillSuccess) {
    patchState({
      preautorisation: {
        ...getState().preautorisation,
        ...preautorisation,
        type: this._appConfigService.typeProjet,
        indCoemprunteurPrincipal: true
      }
    });
  }

  @Action([
    SaveAiguilleurMultiproprietaire,
    SaveAiguilleurResidencePrincipale,
    SaveAiguilleurSalarie,
    SaveEtapeConsentement,
    SaveAiguilleurCitoyennete
  ])
  saveValueInState({ patchState }, value) {
    patchState({
      ...value
    });
  }

  @Action([
    SaveEtapeRevenuBrut,
    SaveEtapeCoemprunteur,
    SaveEtapeCoordonnees,
    SaveEtapeHypotheque,
    SaveEtapePrixPropriete,
    SaveEtapeRevenuLocatif,
    SaveEtapeMiseDeFonds,
    SaveEtapeAchatADeux,
    SaveEtapeAchatMemeCoemprunteur
  ])
  saveQuestionFormulaire({ patchState, getState }, action: { payload: Partial<PreautorisationModel> }) {
    patchState({
      preautorisation: {
        ...getState().preautorisation,
        ...action.payload
      }
    });
  }

  @Action(SaveEtapeTypePropriete)
  saveEtapeTypePropriete(
    { patchState, getState },
    { typePropriete, typeMultiplex, nbrLogements, indHabitableHiver }: SaveEtapeTypePropriete
  ) {
    patchState({
      typePropriete,
      typeMultiplex,
      preautorisation: {
        ...getState().preautorisation,
        nbrLogements,
        indHabitableHiver
      }
    });

    if (typePropriete === TypeProprieteEnum.Maison) {
      patchState({
        preautorisation: {
          ...getState().preautorisation,
          codeFrequenceRevenuLocatifEstime: null,
          revenuLocatifEstime: null
        }
      });
    }
  }

  @Action(ValiderPreadmissibilite)
  async validerPreadmissibilite({ dispatch, patchState }) {
    dispatch(new ShowLoader());

    try {
      const preadmissibilite = await this._preautorisationApiService.validatePreadmissibilite().toPromise();

      if (preadmissibilite.statut === StatutPreadmissibilite.Refuser) {
        patchState({
          preautorisation: { statut: StatusPreautorisation.RefuseeSimplifie, id: preadmissibilite.id },
          isPreautLoaded: true,
          currentStep: null
        });
        this._cybermetrieService.pushCompletionEvent(StatusPreautorisation.RefuseeSimplifie);
        dispatch(new NavigatePageResultat());
      } else {
        patchState({
          numeroDemande: preadmissibilite.numeroDemande
        });
        dispatch(new GoNextStep());
      }
    } catch {
      dispatch(new NavigatePagePannePreadmissibilite());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(ValiderCodeInvitationCourriel)
  async validerCodeInvitation({ dispatch }, { codeInvitationCourriel }: ValiderCodeInvitationCourriel) {
    dispatch(new ShowLoader());
    try {
      const resultatValidation = await this._preautorisationApiService.validateCodeInvitationCoemprunteur(
        codeInvitationCourriel
      );
      dispatch(new ValiderCodeInvitationCourrielSuccess(resultatValidation));
    } catch (error) {
      dispatch(new ValiderCodeInvitationCourrielFailed(error));
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(ValiderCodeInvitationCourrielSuccess)
  completerDemande({ dispatch, patchState, getState }, { preautorisation }: ValiderCodeInvitationCourrielSuccess) {
    patchState({
      isPreautLoaded: true,
      preautorisation: {
        ...getState().preautorisation,
        ...preautorisation,
        indCoemprunteurPrincipal: false,
        indCoemprunteur: true,
        indCoemprunteurProprieteActuelle: null,
        statut: null
      }
    });
    dispatch(new NavigatePageFormulaire());
  }

  @Action(ValiderCodeInvitationCourrielFailed)
  gererErreurValidation({ dispatch, patchState, getState }, { response }: ValiderCodeInvitationCourrielFailed) {
    if (response.status === 400) {
      const validationError = response.error as ErreurValidationModel;
      if (validationError.codeErreur) {
        patchState({ validationError: validationError });
      }
    } else {
      dispatch(new TriggerErrorPage());
    }
  }

  @Action(RefuserProjet)
  async refuserProjet({ dispatch, getState }) {
    dispatch(new ShowLoader());
    const preautorisation = getState().preautorisation;
    try {
      await this._preautorisationApiService.deletePreautorisation(preautorisation.id, preautorisation.codeInvitation);
      dispatch(new RefuserProjetSuccess());
    } catch {
      dispatch(new RefuserProjetFailed());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(RefuserProjetSuccess)
  async refuserProjetSuccess({ dispatch }) {
    dispatch(new ResetState());
    dispatch(new NavigateDashboard());
  }

  @Action(Submit)
  async submit({ dispatch, getState }) {
    dispatch(new ShowLoader());

    const numeroDemande = getState().numeroDemande;
    const preautorisation = getState().preautorisation;

    try {
      const result = await this._preautorisationApiService.submitPreautorisation(
        preautorisation,
        getState().idConsentement,
        numeroDemande
      );
      if (result && result.id) {
        dispatch(new SubmitSuccess(result));
      } else {
        dispatch(new SubmitFailed(result.codeErreur));
      }
    } catch {
      dispatch(new SubmitFailed());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action([SubmitSuccess, AccepterContreOffreSuccess, RefuserContreOffreSuccess, SelectionnerTauxSuccess])
  async submitSuccess({ patchState, dispatch }, { preautorisation }: SubmitSuccess) {
    patchState({ preautorisation });
    dispatch(new NavigatePageResultat());
    this._cybermetrieService.pushCompletionEvent(preautorisation.statut);
  }

  @Action([
    SubmitFailed,
    AccepterContreOffreFailed,
    RefuserContreOffreFailed,
    SelectionnerTauxFailed,
    RefuserProjetFailed
  ])
  async submitFailed({ getState, dispatch }, { codeErreur }: SubmitFailed) {
    const state = getState();
    if (
      codeErreur === ErreurPreautCoemprunteur.Annule &&
      PreautorisationSelectors.typeEmprunteur(state) === TypeEmprunteurEnum.Coemprunteur2
    ) {
      dispatch(new NavigatePageAnnuleParCoemprunteur());
      dispatch(new ResetState());
    } else {
      dispatch(new TriggerErrorPage());
    }
  }

  @Action(Load)
  async load({ patchState }) {
    try {
      const preautorisation = await this._preautorisationApiService.getPreautorisation();
      patchState({
        preautorisation
      });
    } catch (error) {
      /* 404 si aucune preaut rien à faire */
    } finally {
      patchState({ isLoaded: true });
    }
  }

  @Action(EditStep)
  editStep({ patchState, getState }, { step }: EditStep) {
    if (!getState().isModifying) {
      patchState({
        stateAvantModification: cloneDeep(getState())
      });
    }

    patchState({
      isModifying: true,
      currentStep: step
    });
  }

  @Action(GoToNextInvalidStepOrCompleteEdit)
  goToNextInvalidStepOrCompleteEdit({ dispatch }) {
    if (!this._miseDeFondsService.isMiseDeFondsValid()) {
      dispatch(new EditStep(StepEnum.ETAPE_MISE_DE_FONDS));
    } else {
      dispatch(new CompleteEditStep());
    }
  }

  @Action(CompleteEditStep)
  completeEditStep({ patchState }) {
    patchState({
      currentStep: StepEnum.ETAPE_RECAP,
      isModifying: false
    });
    window.scroll(0, 0);
  }

  @Action(CancelEditStep)
  cancelEditStep({ patchState, getState }) {
    patchState({
      ...getState().stateAvantModification,
      currentStep: StepEnum.ETAPE_RECAP,
      isModifying: false,
      stateAvantModification: null
    });
  }

  @Action([CancelPreautorisation])
  async cancelPreautorisation({ getState, dispatch }) {
    const state = getState();
    dispatch(new ShowLoader());
    try {
      if (state.preautorisation.id) {
        const statut = state.preautorisation.statut;
        if (statut === StatusPreautorisation.RefuseeSimplifie || statut === StatusPreautorisation.ContreOffreRefusee) {
          this._cybermetrieService.pushClickEvent('eof - annulez votre preautorisation');
        } else if (statut === StatusPreautorisation.EnAttenteSelectionTaux) {
          this._cybermetrieService.pushClickEvent('annuler - choix de taux');
        } else {
          this._cybermetrieService.pushClickEvent('annulez votre preautorisation');
        }

        await this._preautorisationApiService.deletePreautorisation(state.preautorisation.id);
      }
      dispatch(new ResetState());
    } catch {
      dispatch(new TriggerErrorPage());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(CancelWithConfirmation)
  async cancelWithConfirmation({ dispatch }) {
    // Retarder le chargement du component pour éviter les erreur d'import webpack
    const ModaleAnnulerComponent = (await import('../components/modale-annuler/modale-annuler.component'))
      .ModaleAnnulerComponent;

    this._openModalService
      .openModalDSD(ModaleAnnulerComponent)
      .closed.pipe(first())
      .subscribe(async result => {
        if (result === ModaleAnnulerResultatEnum.Annuler) {
          await dispatch(new CancelPreautorisation()).toPromise();
          dispatch(new NavigateDashboard());
        }
      });
  }

  @Action(ResetState)
  async resetState({ patchState }) {
    patchState({ ...defaultState, isLoaded: true });
    patchState({ preautorisation: { type: this._appConfigService.typeProjet } });
  }

  @Action(AccepterContreOffre)
  async accepterContreOffre({ dispatch }) {
    dispatch(new ShowLoader());
    try {
      const preautorisation = await this._preautorisationApiService.accepterContreOffre();
      dispatch(new AccepterContreOffreSuccess(preautorisation));
    } catch {
      dispatch(new AccepterContreOffreFailed());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(RefuserContreOffre)
  async refuserContreOffre({ dispatch }) {
    dispatch(new ShowLoader());
    try {
      const preautorisation = await this._preautorisationApiService.refuserContreOffre();
      dispatch(new RefuserContreOffreSuccess(preautorisation));
    } catch {
      dispatch(new RefuserContreOffreFailed());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(SelectionnerTaux)
  async selectionnerTaux({ dispatch }, { idOffreTarificationBancaire }: SelectionnerTaux) {
    dispatch(new ShowLoader());
    try {
      const preautorisation = await this._preautorisationApiService.selectionnerTaux(idOffreTarificationBancaire);
      dispatch(new SelectionnerTauxSuccess(preautorisation));
    } catch {
      dispatch(new SelectionnerTauxFailed());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(PatchDebug)
  async patchDebug({ getState, patchState }, { preautorisation }: PatchDebug) {
    const state = getState();

    if (state.preautorisation && state.preautorisation.resultat) {
      state.preautorisation.resultat.taux = [];
    }

    patchState({
      preautorisation: merge(state.preautorisation, preautorisation)
    });
  }
}
